import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast, Toaster } from "sonner";

import Header from "../components/Header";
import TagInput from "../components/TagInput";
import { API, api, formatApiErrorDetail } from "../lib/api";

const SORTS = ["newest", "starting_soon", "tjm_desc", "tjm_asc"];
const SENIORITY = ["junior", "confirmé", "senior", "expert"];
const REMOTE = ["onsite", "hybrid", "remote"];

function fmtDate(iso) {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}
function fmtTjm(m) {
  if (m.tjm_type === "fixed") return `$${m.tjm_min}`;
  return `$${m.tjm_min} – $${m.tjm_max}`;
}

export default function Marketplace() {
  const { t } = useTranslation();
  const [filters, setFilters] = useState({
    q: "",
    skills: [],
    seniority: "",
    remote_policy: "",
    tjm_min: "",
    tjm_max: "",
    start_after: "",
    duration_min: "",
    duration_max: "",
    location: "",
    distance_km: 50,
  });
  const [sort, setSort] = useState("newest");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const params = useMemo(() => {
    const p = { sort };
    if (filters.q) p.q = filters.q;
    if (filters.skills.length) p.skills = filters.skills.join(",");
    if (filters.seniority) p.seniority = filters.seniority;
    if (filters.remote_policy) p.remote_policy = filters.remote_policy;
    if (filters.tjm_min) p.tjm_min = filters.tjm_min;
    if (filters.tjm_max) p.tjm_max = filters.tjm_max;
    if (filters.start_after) p.start_after = filters.start_after;
    if (filters.duration_min) p.duration_min = filters.duration_min;
    if (filters.duration_max) p.duration_max = filters.duration_max;
    if (filters.location) {
      p.location = filters.location;
      p.distance_km = filters.distance_km;
    }
    return p;
  }, [filters, sort]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/missions/marketplace", { params });
      setItems(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("common.error_generic"));
    } finally {
      setLoading(false);
    }
  }, [params, t]);

  useEffect(() => {
    load();
  }, [load]);

  const setF = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  return (
    <div className="min-h-screen bg-slate-50" data-testid="marketplace-page">
      <Toaster richColors position="top-right" />
      <Header />
      <main className="mx-auto max-w-6xl px-6 py-10">
        <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-indigo-700">
              {t("marketplace.eyebrow")}
            </p>
            <h1 className="mt-1 text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">
              {t("marketplace.title")}
            </h1>
            <p className="mt-2 text-sm text-slate-600">{t("marketplace.subtitle")}</p>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600">
              {t("marketplace.sort_by")}
            </label>
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
              data-testid="marketplace-sort"
            >
              {SORTS.map((s) => (
                <option key={s} value={s}>{t(`marketplace.sort_${s}`)}</option>
              ))}
              <option value="distance" disabled={!filters.location}>{t("marketplace_extra.sort_distance")}</option>
            </select>
          </div>
        </div>

        <div className="grid gap-6 md:grid-cols-[280px_1fr]">
          {/* Filter sidebar */}
          <aside className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="marketplace-filters">
            <h2 className="text-sm font-semibold text-slate-900">{t("marketplace.filters")}</h2>
            <div className="mt-4 space-y-4 text-sm">
              <FilterField label={t("marketplace.f_search")}>
                <input
                  value={filters.q}
                  onChange={(e) => setF("q", e.target.value)}
                  placeholder={t("marketplace.f_search_placeholder")}
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                  data-testid="filter-q"
                />
              </FilterField>
              <FilterField label={t("marketplace.f_skills")}>
                <TagInput
                  value={filters.skills}
                  onChange={(v) => setF("skills", v)}
                  placeholder={t("roster.skills_placeholder")}
                  testId="filter-skills"
                  containerTestId="filter-skills-container"
                />
              </FilterField>
              <FilterField label={t("marketplace.f_seniority")}>
                <select
                  value={filters.seniority}
                  onChange={(e) => setF("seniority", e.target.value)}
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                  data-testid="filter-seniority"
                >
                  <option value="">{t("marketplace.f_any")}</option>
                  {SENIORITY.map((s) => (
                    <option key={s} value={s}>{t(`roster.seniority_${s}`)}</option>
                  ))}
                </select>
              </FilterField>
              <FilterField label={t("marketplace.f_remote")}>
                <select
                  value={filters.remote_policy}
                  onChange={(e) => setF("remote_policy", e.target.value)}
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                  data-testid="filter-remote"
                >
                  <option value="">{t("marketplace.f_any")}</option>
                  {REMOTE.map((r) => (
                    <option key={r} value={r}>{t(`roster.remote_${r}`)}</option>
                  ))}
                </select>
              </FilterField>
              <FilterField label={t("marketplace.f_tjm")}>
                <div className="flex gap-2">
                  <input
                    type="number"
                    min={0}
                    value={filters.tjm_min}
                    onChange={(e) => setF("tjm_min", e.target.value)}
                    placeholder={t("marketplace.f_min")}
                    className="w-full rounded-lg border border-slate-300 bg-white px-2 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                    data-testid="filter-tjm-min"
                  />
                  <input
                    type="number"
                    min={0}
                    value={filters.tjm_max}
                    onChange={(e) => setF("tjm_max", e.target.value)}
                    placeholder={t("marketplace.f_max")}
                    className="w-full rounded-lg border border-slate-300 bg-white px-2 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                    data-testid="filter-tjm-max"
                  />
                </div>
              </FilterField>
              <FilterField label={t("marketplace.f_start_after")}>
                <input
                  type="date"
                  value={filters.start_after}
                  onChange={(e) => setF("start_after", e.target.value)}
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                  data-testid="filter-start"
                />
              </FilterField>
              <FilterField label={t("marketplace.f_duration")}>
                <div className="flex gap-2">
                  <input
                    type="number"
                    min={1}
                    value={filters.duration_min}
                    onChange={(e) => setF("duration_min", e.target.value)}
                    placeholder={t("marketplace.f_min")}
                    className="w-full rounded-lg border border-slate-300 bg-white px-2 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                    data-testid="filter-duration-min"
                  />
                  <input
                    type="number"
                    min={1}
                    value={filters.duration_max}
                    onChange={(e) => setF("duration_max", e.target.value)}
                    placeholder={t("marketplace.f_max")}
                    className="w-full rounded-lg border border-slate-300 bg-white px-2 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                    data-testid="filter-duration-max"
                  />
                </div>
              </FilterField>
              <FilterField label={t("marketplace_extra.f_location")}>
                <input
                  value={filters.location}
                  onChange={(e) => setF("location", e.target.value)}
                  placeholder={t("marketplace_extra.f_location_placeholder")}
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                  data-testid="filter-location"
                />
              </FilterField>
              <FilterField label={`${t("marketplace_extra.f_radius")} (${filters.distance_km} ${t("marketplace_extra.distance_hint")})`}>
                <input
                  type="range"
                  min={5}
                  max={200}
                  step={5}
                  value={filters.distance_km}
                  onChange={(e) => setF("distance_km", Number(e.target.value))}
                  disabled={!filters.location}
                  className="w-full disabled:opacity-40"
                  data-testid="filter-radius"
                />
              </FilterField>
              <button
                type="button"
                onClick={() =>
                  setFilters({
                    q: "",
                    skills: [],
                    seniority: "",
                    remote_policy: "",
                    tjm_min: "",
                    tjm_max: "",
                    start_after: "",
                    duration_min: "",
                    duration_max: "",
                    location: "",
                    distance_km: 50,
                  })
                }
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50"
                data-testid="filter-reset"
              >
                {t("marketplace.reset")}
              </button>
            </div>
          </aside>

          <section>
            {loading && (
              <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center text-slate-500 shadow-sm">
                {t("common.loading")}
              </div>
            )}
            {!loading && items.length === 0 && (
              <div
                className="rounded-2xl border border-dashed border-slate-300 bg-white p-12 text-center shadow-sm"
                data-testid="marketplace-empty"
              >
                <div className="mx-auto mb-4 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-500 ring-1 ring-indigo-100">
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 10a7 7 0 11-14 0 7 7 0 0114 0z"/></svg>
                </div>
                <h3 className="text-lg font-semibold text-slate-900">{t("marketplace.empty_title")}</h3>
                <p className="mt-2 text-sm text-slate-600">{t("marketplace.empty_body")}</p>
                <p className="mt-4 text-xs text-slate-500">{t("marketplace_extra.self_hint")}</p>
              </div>
            )}
            {!loading && items.length > 0 && (
              <ul className="grid gap-4" data-testid="marketplace-list">
                {items.map((m) => (
                  <MarketplaceCard key={m.id} mission={m} />
                ))}
              </ul>
            )}
          </section>
        </div>
      </main>
    </div>
  );
}

function FilterField({ label, children }) {
  return (
    <div>
      <label className="mb-1.5 block text-xs font-medium text-slate-600">{label}</label>
      {children}
    </div>
  );
}

function MarketplaceCard({ mission }) {
  const { t } = useTranslation();
  const posterHasLogo = !!mission.poster.logo_url;
  return (
    <li className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition-shadow hover:shadow-md" data-testid={`market-card-${mission.id}`}>
      <Link to={`/marketplace/${mission.id}`} className="block">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex-1 min-w-[280px]">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-base font-semibold text-slate-900 md:text-lg">{mission.title}</h3>
              {mission.visibility === "invite_only" && (
                <span className="rounded-full bg-fuchsia-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-fuchsia-700 ring-1 ring-fuchsia-100">
                  {t("mission.visibility_invite_only")}
                </span>
              )}
              {typeof mission.distance_km === "number" && (
                <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-700 ring-1 ring-emerald-100">
                  {mission.distance_km} km
                </span>
              )}
            </div>
            <div className="mt-1 flex items-center gap-2 text-xs text-slate-500">
              <span className="inline-flex items-center gap-1.5">
                {posterHasLogo ? (
                  <img src={`${API}/orgs/${mission.poster.id}/logo`} alt="" className="h-4 w-4 rounded-sm object-cover" />
                ) : (
                  <span className="inline-flex h-4 w-4 items-center justify-center rounded-sm bg-slate-200 text-[9px] font-bold text-slate-600">
                    {(mission.poster.name || "").slice(0, 1).toUpperCase()}
                  </span>
                )}
                <span data-testid={`market-card-poster-${mission.id}`}>{mission.poster.name}</span>
              </span>
              <span
                className={
                  "rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ring-1 " +
                  (mission.posted_by_role === "agency"
                    ? "bg-indigo-50 text-indigo-700 ring-indigo-100"
                    : "bg-emerald-50 text-emerald-700 ring-emerald-100")
                }
                data-testid={`market-card-poster-role-${mission.id}`}
              >
                {t(`dashboard.role_badge_${mission.posted_by_role}`)}
              </span>
              <span>·</span>
              <span>{t(`roster.seniority_${mission.seniority}`)}</span>
              <span>·</span>
              <span>{t(`roster.remote_${mission.remote_policy}`)}</span>
              <span>·</span>
              <span>{mission.location_city}</span>
            </div>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {(mission.required_skills || []).slice(0, 8).map((s) => (
                <span key={s} className="rounded-md bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700">
                  {s}
                </span>
              ))}
            </div>
            <p className="mt-3 line-clamp-2 text-sm text-slate-600">{mission.description}</p>
          </div>
          <div className="text-right text-xs text-slate-600">
            <div className="text-lg font-semibold text-slate-900">{fmtTjm(mission)}</div>
            <div className="mt-1">
              {t("mission.start")} {fmtDate(mission.start_date)}
            </div>
            <div className="mt-0.5">
              {mission.duration_months} {t("mission.months")}
            </div>
          </div>
        </div>
      </Link>
    </li>
  );
}
